import { ActivityIndicator, TextInput, Pressable, View } from 'react-native';
import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Text } from '@/components/Text';
import theme from '@/config/theme';
import { styles } from './TwoFaDialog.styles';

export interface TwoFaDialogRef {
  updateError: (err: 'invalid' | 'expired') => void;
  resetOTP: () => void;
}

interface TwoFaDialogProps {
  invalidCode?: boolean;
  expiredCode?: boolean;
  onSubmit: (OTP: string) => void;
  onPressSendCodeAgain: () => void;
}

export const TwoFaDialog = forwardRef<TwoFaDialogRef, TwoFaDialogProps>(
  (
    {
      invalidCode = false,
      expiredCode = false,
      onSubmit,
      onPressSendCodeAgain,
    }: TwoFaDialogProps,
    ref,
  ) => {
    const [OTP, setOTP] = useState('');
    const [errorState, setErrorState] = useState<
      'none' | 'invalid' | 'expired'
    >('none');
    const [textInputFocused, setTextInputFocused] = useState(false);
    const [codeResent, setCodeResent] = useState(false);
    const textInputRef = useRef<TextInput>(null);

    useEffect(() => {
      if (invalidCode) {
        setErrorState('invalid');
      } else if (expiredCode) {
        setErrorState('expired');
      } else {
        setErrorState('none');
      }
    }, [invalidCode, expiredCode]);

    useImperativeHandle(ref, () => ({
      resetOTP: () => {
        setOTP('');
        setErrorState('none');
      },
      updateError: (err: 'invalid' | 'expired') => {
        setErrorState(err);
      },
    }));

    useEffect(() => {
      if (OTP.length !== 6) {
        return;
      }
      textInputRef.current?.blur();
      onSubmit(OTP);
    }, [OTP]);

    const handleResendCode = () => {
      setCodeResent(true);
      setTimeout(() => {
        setCodeResent(false);
      }, 3000);
      onPressSendCodeAgain();
    };

    return (
      <View style={styles.container}>
        <Text style={styles.title}>Sicherheitsüberprüfung</Text>
        {errorState === 'expired' && (
          <Text style={styles.errorText}>
            Der eingegebene Code ist nicht mehr gültig. Bitte erstellen Sie
            einen neuen Token.
          </Text>
        )}
        {errorState === 'invalid' && (
          <Text style={styles.errorText}>
            Der eingegebene Code ist ungültig. Bitte versuchen Sie es erneut.
          </Text>
        )}
        {errorState === 'none' && (
          <Text style={styles.infoText}>
            Aus Sicherheitsgründen haben wir Ihnen einen Code per E-Mail
            gesendet. Bitte geben Sie diesen Code hier ein, um sich anzumelden.
          </Text>
        )}

        <View style={styles.centered}>
          <View style={styles.otpRow}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Pressable
                key={index}
                style={[
                  styles.otpCell,
                  {
                    borderColor: textInputFocused
                      ? theme.colors.black
                      : theme.colors.twGrey,
                  },
                ]}
                onPress={() => textInputRef.current?.focus()}
              >
                <Text style={styles.otpCellText}>
                  {OTP.split('')[index] ?? ''}
                </Text>
              </Pressable>
            ))}
            {OTP.length === 6 && (
              <View style={styles.loadingIndicator}>
                <ActivityIndicator color={theme.colors.black} size={26} />
              </View>
            )}
          </View>
          <View>
            <Pressable onPress={handleResendCode}>
              <Text
                style={
                  codeResent ? styles.resendCodeSent : styles.resendCodeActive
                }
              >
                Code erneut anfordern
              </Text>
            </Pressable>
          </View>
          {codeResent ? (
            <Text style={styles.codeSentConfirmation}>
              Ein neuer Code wurde an Ihre E-Mail-Adresse gesendet.
            </Text>
          ) : null}
        </View>

        <TextInput
          allowFontScaling={false}
          onFocus={() => setTextInputFocused(true)}
          onBlur={() => setTextInputFocused(false)}
          ref={textInputRef}
          style={styles.hiddenInput}
          keyboardType="number-pad"
          maxLength={6}
          value={OTP}
          onChangeText={(text) => setOTP(text.replace(/[^0-9]/g, ''))}
        />
      </View>
    );
  },
);

TwoFaDialog.displayName = 'TwoFaDialog';
